import {
  addOrReplace,
  assignIfExists,
  assignProps,
  isValidFilter,
  removeItemByCode,
  selectableResult,
} from '@/utils/resultsHelper';
import { responseError } from '@/utils/errors';
import { states } from '@/constants/lifecycle';
import withDraft from '@/utils/draftHelper';
import {
  collectionAssigningDataset,
  datasetCollection,
  datasetCollectionsState,
  findDatasetCollection,
  newDatasetCollection,
  removeDatasetCollection,
} from './state';
import actions from '../constants';
import findDatasetCollectionEntities from '../helper';

const hideRemoveDatasetCollectionConfirmation = withDraft((draft) => {
  draft.removeDatasetCollectionConfirmation = removeDatasetCollection();
});

const updateDatasetCollectionsAssignableProperties = withDraft((draft, { properties }) => {
  assignIfExists(draft.collectionAssigningDataset, properties);
});

const addDatasetCollections = withDraft((draft) => {
  draft.collectionAssigningDataset.saveStatus = states.PENDING;
});

const resetDatasetCollections = withDraft((draft) => {
  draft.collectionAssigningDataset = collectionAssigningDataset();
});

const fetchDatasetCollections = withDraft((draft, action) => {
  const item = findDatasetCollection(draft, action);
  if (item) {
    item.readStatus = states.PENDING;
    item.readErrors = [];
  } else {
    draft.datasetCollections[action.category].results.push({
      code: action.code,
      readStatus: states.PENDING,
      readErrors: [],
    });
  }
});

const showDatasetCollections = withDraft((draft, action) => {
  const item = findDatasetCollection(draft, action);
  assignProps(item, {
    readStatus: states.SUCCESS,
    readErrors: [],
    name: action.name,
  });
  draft.datasetCollections.entities = addOrReplace(
    draft.datasetCollections.entities,
    { code: action.code, results: action.results.items.map((c) => ({ ...datasetCollection(), ...c })) },
  );
});

const showDatasetCollectionsError = withDraft((draft, action) => {
  const item = findDatasetCollection(draft, action);
  item.readStatus = states.ERROR;
  item.readErrors = [responseError(action)];
});

const showRemoveDatasetCollectionConfirmation = withDraft((draft, action) => {
  draft.removeDatasetCollectionConfirmation = {
    open: true,
    code: action.code,
    category: action.category,
    collectionCode: action.collectionCode,
    name: action.name,
  };
});

const removeDatasetCollectionState = withDraft((draft, action) => {
  const item = findDatasetCollection(draft, action);
  item.saveStatus = states.PENDING;
  item.saveErrors = [];
  draft.removeDatasetCollectionConfirmation = removeDatasetCollection();
});

const removeDatasetCollectionSuccess = withDraft((draft, action) => {
  const item = findDatasetCollection(draft, action);
  item.saveStatus = states.SUCCESS;
  const collections = findDatasetCollectionEntities(draft.datasetCollections, action.code);
  collections.results = collections.results.filter((collection) => collection.code !== action.collectionCode);
});

const removeDatasetCollectionError = withDraft((draft, action) => {
  const item = findDatasetCollection(draft, action);
  item.saveStatus = states.ERROR;
  item.saveErrors = [responseError(action)];
});

const showAddDatasetCollections = withDraft((draft, action) => {
  const item = findDatasetCollection(draft, action);
  draft.collectionAssigningDataset.open = true;
  draft.collectionAssigningDataset.category = action.category;
  draft.collectionAssigningDataset.code = action.code;
  draft.collectionAssigningDataset.name = action.name;
  item.collectionsToAdd = datasetCollectionsState(10000, 0, true);
  item.unassignedCollections = datasetCollectionsState(20, 3, true);
});

const hideAddDatasetCollections = withDraft((draft, action) => {
  const item = findDatasetCollection(draft, action);
  draft.collectionAssigningDataset = collectionAssigningDataset();
  item.collectionsToAdd = datasetCollectionsState(10000, 0, true);
  item.unassignedCollections = datasetCollectionsState(20, 3, true);
});

const fetchDatasetCollectionsAssignable = withDraft((draft, action) => {
  const item = findDatasetCollection(draft, action);
  item.unassignedCollections.filter = action.filter;
  item.unassignedCollections.results = [];
  if (isValidFilter(action.filter, item.unassignedCollections)) {
    item.unassignedCollections.readStatus = states.PENDING;
    item.unassignedCollections.readErrors = [];
  }
});

const updateDatasetUnassignedCollectionsProperties = withDraft((draft, action) => {
  const item = findDatasetCollection(draft, action);
  assignIfExists(item.unassignedCollections, action.properties);
});

const fetchDatasetCollectionsAssignableSuccess = withDraft((draft, action) => {
  const item = findDatasetCollection(draft, action);
  const results = action.results.map(selectableResult);
  item.unassignedCollections.readStatus = states.SUCCESS;
  item.unassignedCollections.readErrors = [];
  item.unassignedCollections.results = results;
  item.unassignedCollections.original = results;
  item.unassignedCollections.count = action.resultsCount;
});

const fetchDatasetCollectionsAssignableError = withDraft((draft, action) => {
  const item = findDatasetCollection(draft, action);
  item.unassignedCollections.readStatus = states.ERROR;
  item.unassignedCollections.readErrors = [responseError(action)];
});

const addDatasetCollectionsAssignable = withDraft((draft, action) => {
  const item = findDatasetCollection(draft, action);
  const collectionsToAdd = draft.collectionAssigningDataset.collectionsToAdd.map((c) => newDatasetCollection(c));
  draft.collectionAssigningDataset.collectionsToAdd = [];
  item.collectionsToAdd.results = item.collectionsToAdd.results.concat(collectionsToAdd);
  item.unassignedCollections.filter = '';
});

const removeDatasetCollectionAssignable = withDraft((draft, action) => {
  const item = findDatasetCollection(draft, action);
  item.collectionsToAdd.results = removeItemByCode(item.collectionsToAdd.results, { code: action.collectionCode });
});

export default {
  [actions.ADD_DATASET_COLLECTIONS]: addDatasetCollections,
  [actions.ADD_DATASET_COLLECTIONS_ERROR]: resetDatasetCollections,
  [actions.ADD_DATASET_COLLECTIONS_SUCCESS]: resetDatasetCollections,
  [actions.ADD_DATASET_COLLECTIONS_ASSIGNABLE]: addDatasetCollectionsAssignable,
  [actions.FETCH_DATASET_COLLECTIONS]: fetchDatasetCollections,
  [actions.FETCH_DATASET_COLLECTIONS_ASSIGNABLE]: fetchDatasetCollectionsAssignable,
  [actions.FETCH_DATASET_COLLECTIONS_ASSIGNABLE_ERROR]: fetchDatasetCollectionsAssignableError,
  [actions.FETCH_DATASET_COLLECTIONS_ASSIGNABLE_SUCCESS]: fetchDatasetCollectionsAssignableSuccess,
  [actions.HIDE_ADD_DATASET_COLLECTIONS]: hideAddDatasetCollections,
  [actions.HIDE_REMOVE_DATASET_COLLECTION_CONFIRMATION]: hideRemoveDatasetCollectionConfirmation,
  [actions.REMOVE_DATASET_COLLECTION]: removeDatasetCollectionState,
  [actions.REMOVE_DATASET_COLLECTION_ASSIGNABLE]: removeDatasetCollectionAssignable,
  [actions.REMOVE_DATASET_COLLECTION_ERROR]: removeDatasetCollectionError,
  [actions.REMOVE_DATASET_COLLECTION_SUCCESS]: removeDatasetCollectionSuccess,
  [actions.SHOW_ADD_DATASET_COLLECTIONS]: showAddDatasetCollections,
  [actions.SHOW_DATASET_COLLECTIONS]: showDatasetCollections,
  [actions.SHOW_DATASET_COLLECTIONS_ERROR]: showDatasetCollectionsError,
  [actions.SHOW_REMOVE_DATASET_COLLECTION_CONFIRMATION]: showRemoveDatasetCollectionConfirmation,
  [actions.UPDATE_DATASET_COLLECTIONS_ASSIGNABLE_PROPERTIES]: updateDatasetCollectionsAssignableProperties,
  [actions.UPDATE_DATASET_UNASSIGNED_COLLECTIONS_PROPERTIES]: updateDatasetUnassignedCollectionsProperties,
};
